/**
 * InspirationFeed.jsx — Phase 152, updated Phase 153. Curated Studio
 * Inspiration Feed. Reads `GET /inspiration-feed` (a 3-hourly background
 * aggregator over curated design/hardware RSS sources + Product Hunt,
 * sorted most-recent-first). Minimal list layout: bold title + 2-3 line
 * description on the left, image on the right, source icon/name + relative
 * time, a "Read more" link (new tab).
 *
 * Phase 153 — "Turn into Post": creates a real Social Posts entry (Idea
 * status, auto-linked to the Marketing & Growth project) directly — no
 * intermediate quick-add step — then hands off to the Home Task Hub so the
 * new idea is visible immediately.
 */
import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { timeAgo } from "@/lib/dashboardHelpers";
import { toast } from "sonner";
import { ExternalLink, Loader2, Megaphone, Sparkles } from "lucide-react";

export default function InspirationFeed({ onPostCreated }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creatingLink, setCreatingLink] = useState(null);

  useEffect(() => {
    let live = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/inspiration-feed");
        if (live) setRows(data || []);
      } catch { if (live) setRows([]); }
      finally { if (live) setLoading(false); }
    })();
    return () => { live = false; };
  }, []);

  const turnIntoPost = async (item) => {
    setCreatingLink(item.link);
    try {
      const { data: marketingProject } = await api.get("/marketing/growth-project");
      const { data: draft } = await api.post("/marketing/drafts", {
        title: item.title,
        caption: item.title,
        channels: ["instagram"],
        project_id: marketingProject?.id || null,
        source: "inspiration",
      });
      toast.success('Added to Social Posts as an "Idea"');
      onPostCreated?.(draft);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCreatingLink(null); }
  };

  return (
    <div data-testid="inspiration-feed">
      <div className="mb-3">
        <div className="label-overline inline-flex items-center gap-1.5"><Sparkles size={11} className="text-[#F54800]" /> Inspiration · live</div>
        <div className="font-display text-lg font-bold text-slate-900 mt-0.5">Industrial design, hardware &amp; UI/UX — freshest first</div>
      </div>

      {loading ? (
        <div className="card-flat p-8 text-center text-slate-400 text-xs" data-testid="inspiration-loading">
          <Loader2 size={16} className="animate-spin mx-auto mb-2" />
          Gathering the latest from your curated sources…
        </div>
      ) : rows.length === 0 ? (
        <div className="card-flat p-8 text-center text-slate-400 text-xs" data-testid="inspiration-empty">
          Nothing fetched yet — the background aggregator refreshes every 3 hours.
        </div>
      ) : (
        <div className="space-y-2.5">
          {rows.map((item, idx) => (
            <InspirationCard key={item.link || idx} item={item} creating={creatingLink === item.link} onTurnIntoPost={turnIntoPost} />
          ))}
        </div>
      )}
    </div>
  );
}

function InspirationCard({ item, creating, onTurnIntoPost }) {
  return (
    <div
      className="card-flat p-4 flex gap-4 items-start hover:border-[#F54800]/40 hover:shadow-sm transition-all"
      data-testid={`inspiration-card-${item.link}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-1.5 text-xs text-slate-500">
          {item.source_icon && <img src={item.source_icon} alt="" className="w-4 h-4 rounded-sm shrink-0" />}
          <span className="font-medium text-slate-600">{item.source}</span>
          <span className="text-slate-300">·</span>
          <span className="text-slate-400">{timeAgo(item.published_at)}</span>
        </div>
        <h3 className="font-display font-bold text-slate-900 text-[15px] leading-snug mb-1.5">{item.title}</h3>
        {item.description && (
          <p className="text-sm text-slate-600 leading-relaxed line-clamp-3 mb-3">{item.description}</p>
        )}
        <div className="flex items-center gap-4 flex-wrap">
          <a
            href={item.link}
            target="_blank"
            rel="noreferrer noopener"
            className="text-xs font-semibold text-[#F54800] hover:underline inline-flex items-center gap-1"
            data-testid={`inspiration-read-more-${item.link}`}
          >
            Read more <ExternalLink size={11} />
          </a>
          <button
            type="button"
            onClick={() => onTurnIntoPost(item)}
            disabled={creating}
            className="text-xs font-medium text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5 transition-colors disabled:opacity-50"
            data-testid={`inspiration-turn-into-post-${item.link}`}
          >
            {creating ? <Loader2 size={12} className="animate-spin" /> : <Megaphone size={12} />} Turn into Post
          </button>
        </div>
      </div>
      {item.image && (
        <img
          src={item.image}
          alt=""
          className="w-24 h-24 sm:w-28 sm:h-28 rounded-sm object-cover shrink-0 bg-slate-100"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      )}
    </div>
  );
}
